/**
 * The World: every byte of simulation state, plus the derived caches that are
 * rebuilt each tick.
 *
 * Anything that must survive a save, a replay or a determinism check lives in
 * the "state" section and is covered by `hashWorld`. Anything in the "derived"
 * section is a pure function of that state and is deliberately excluded.
 */

import { FIRST_BUILD_PHASE_SECONDS, MAP_H, MAP_W, RUBBLE_CLEAR_SECONDS } from './constants.ts';
import type { CompanionHero } from './herotypes.ts';
import {
  addResources,
  alliedWith,
  emptyResources,
  Job,
  OB_SOLID,
  Phase,
  TaxPolicy,
  Team,
  UF,
  UnitState,
  WallKind,
  type PhaseId,
  type ResourceBundle,
} from './types.ts';
import { BuildingStore, ProjectileStore, UnitStore } from './stores.ts';
import { ResourceNodeStore, clearNodesInRect, seedResourceNodes } from './systems/nodes.ts';
import { TileMap, generateMap } from './map.ts';
import { SpatialHash } from './spatial.ts';
import type { SimEvent } from './events.ts';
import { deriveSeed, makeRng, type Rng } from '../core/rng.ts';
import { buildingType, HEARTH_TYPE } from '../data/buildings.ts';
import { unitType, VILLAGER_TYPE } from '../data/units.ts';
import { rankHpMul } from '../data/ranks.ts';
import { faction, factionIndex, type FactionDef } from '../data/factions.ts';
import { race, raceOfFaction } from '../data/races.ts';
import { leader } from '../data/leaders.ts';
import { difficulty, type DifficultyDef } from '../data/difficulty.ts';
import { STARTING_POWERS } from '../data/powers.ts';
import { ITEM_SLOT_COUNT } from '../data/items.ts';
import { bloodlineModBags, bloodlinePowers, bloodlineStart } from './bloodline.ts';
import { FlowField } from './flow.ts';
import { PathCache } from './path.ts';
import { DistrictCache } from './systems/districts.ts';
/**
 * The bindings' arithmetic. `systems/bindings.ts` imports only types, `data/*`
 * and the calendar, precisely so that this file — which half the simulation
 * imports — can read it without adding an edge to the import tangle.
 */
import { GROVE_COMPOUND, groveSeasonMul, isGrove, isHousing, OPENING_SOULS, OPENING_THRALLS } from './systems/bindings.ts';
import { seedDemography } from './systems/demography.ts';
import type { BattleState } from './battle.ts';
import type { CampaignState, VillageType } from './campaign/types.ts';
import type { CrownPurse } from './campaign/commonwealth.ts';

export const Formation = {
  Line: 0,
  Wedge: 1,
  Loose: 2,
  Shieldwall: 3,
} as const;
export type FormationId = (typeof Formation)[keyof typeof Formation];

export const FORMATION_NAMES: Record<number, string> = {
  [Formation.Line]: 'Line',
  [Formation.Wedge]: 'Wedge',
  [Formation.Loose]: 'Loose',
  [Formation.Shieldwall]: 'Shieldwall',
};

export const Stance = {
  Hold: 0,
  Guard: 1,
  Pursue: 2,
  Rally: 3,
} as const;
export type StanceId = (typeof Stance)[keyof typeof Stance];

export const STANCE_NAMES: Record<number, string> = {
  [Stance.Hold]: 'Hold',
  [Stance.Guard]: 'Guard Area',
  [Stance.Pursue]: 'Pursue',
  [Stance.Rally]: 'Retreat & Rally',
};

export const OrderKind = {
  None: 0,
  Move: 1,
  AttackMove: 2,
  Retreat: 3,
} as const;

export interface Company {
  id: number;
  typeIdx: number;
  team: number;
  name: string;
  /** Unit handles. Dead members are compacted out at the end of each tick. */
  members: number[];
  maxSize: number;
  formation: number;
  /** Frontage in models, set by a drag order. 0 = let the formation decide. */
  formationWidth: number;
  stance: number;
  /** Where the formation is anchored, and which way it is pointed. */
  ax: number;
  ay: number;
  facing: number;
  orderX: number;
  orderY: number;
  orderKind: number;
  /** Requested final orientation for a formation drag; null for an ordinary march. */
  orderFacing: number | null;
  /**
   * The way round, when there is something in the way. Tile indices, next
   * waypoint first, consumed from the front as the banner reaches them.
   *
   * Empty for the overwhelming majority of orders and *deliberately* so: an
   * order across open ground is walked in a straight line exactly as it always
   * was, so nothing about the ordinary case changes. It is filled only when the
   * straight line from the banner to the order crosses stone — a wall circuit,
   * a block of roofs — which is the case where marching straight at the order
   * means marching into it and standing there. The men follow their formation
   * slots and the slots follow the banner, so routing the banner is what routes
   * the company; the alternative, a route per model, is four hundred A* searches
   * where one will do.
   *
   * State, not a cache: it decides where the company goes, so it is hashed and
   * it is saved.
   */
  route: number[];
  /**
   * The enemy company this one has been told to kill, or −1.
   *
   * Targeting is otherwise "whatever is nearest", which is right for a line
   * that has been told to hold and wrong for every order a player actually
   * wants to give: break *that* company, shoot *that* engine, ignore the
   * screen in front and answer the thing behind it. Members prefer men of
   * this company over anything nearer, out to a generous radius, and the
   * order clears itself when there is nobody left to kill — a company that
   * kept chasing a memory would be worse than one that never obeyed.
   */
  focusCompany: number;
  /** Veterancy. */
  xp: number;
  rank: number;
  kills: number;
  losses: number;
  /** Set when the company has been wiped; the slot is kept for the roster UI. */
  dead: boolean;
  /** Ticks since the company last took a casualty — drives the rout check. */
  lastLossTick: number;
  cohesion: number;
  /** Invaders only: 0 = march on the Hearth, 1 = raid the economy. */
  flowKind: number;
  /** Invaders only: the gate this company entered from, for the scouting panel. */
  gate: number;
  /**
   * The section of curtain this company has been ordered to hold, or −1.
   *
   * One tile, not a list: it is where the *order* was pointed, and the men
   * spread out from it along the run — see `assignPosts` in
   * `systems/garrison.ts`. Storing the span instead would be a second copy of
   * something the wall layer already knows, and it would go stale the moment a
   * section fell.
   */
  garrisonTile: number;
  /** Set on the wave's named champion. */
  boss: boolean;
  /**
   * The Second Wind has already fired for this company this Siege.
   *
   * Per company and per Siege, both deliberately: the relic promises "the first
   * time each Siege a company would be wiped", so a company cannot be raised
   * twice in one wave and every company gets its own chance.
   */
  usedSecondWind: boolean;
}

/**
 * Global multipliers. Relics and tech nodes write here; systems read here.
 * Keeping every run-modifier in one flat numeric bag means a new relic is a
 * data change, and means the balance harness can print the whole modifier state
 * of a run in one line.
 */
export interface Mods {
  unitDamage: number;
  unitHp: number;
  moveSpeed: number;
  attackSpeed: number;
  wallHp: number;
  towerDamage: number;
  towerRange: number;
  buildSpeed: number;
  buildCost: number;
  /**
   * How much of the cart-ride penalty is actually paid, 1 for all of it.
   *
   * A multiplier on `HAUL_BUILD_PENALTY` rather than a second `buildSpeed`,
   * because it is a different thing: `buildSpeed` is how fast masons work and
   * this is whether the materials are there when they reach for them. Bought
   * down by the Carters' Road in `data/tech.ts` — the one node whose value
   * depends on how spread out the crown is, which is the only node in the tree
   * of which that is true.
   */
  haulPenalty: number;
  /**
   * Flat storage from tech and relics, in resource units.
   *
   * `resCap` is *derived* — `recomputeStorage` rebuilds it from scratch every
   * time a building finishes, is destroyed, is demolished or is upgraded — so
   * anything that wrote `w.resCap += n` directly was erased seconds later. It
   * has to live in the modifier bag with everything else that outlives a
   * building.
   */
  storage: number;
  willRegen: number;
  willMax: number;
  powerDamage: number;
  powerCooldown: number;
  powerRadius: number;
  soulGain: number;
  resourceRate: number;
  grainUpkeep: number;
  popCap: number;
  archerRange: number;
  cavalryCharge: number;
  veterancyRate: number;
  thorns: number;
  lifesteal: number;
  siegeResist: number;
  moraleFloor: number;
  reinforceCost: number;
  corpseRise: number;
  /** Flat extra Will granted whenever a wave ends. */
  willOnWaveEnd: number;
  /**
   * The fraction of a wiped player company that gets back up, once per Siege.
   * 0 disables it. Read by `stepCompanies`.
   */
  secondWind: number;
  /**
   * Flat Mana capacity, in the units `w.manaMax` is measured in.
   *
   * Additive, and it lives in the bag rather than on `World` for exactly the
   * reason `storage` does — read the note there, it is the same trap. `manaMax`
   * is *derived*: `recomputeManaCap` rebuilds it from scratch every time a
   * building finishes, is destroyed, is demolished or is upgraded, so anything
   * writing `w.manaMax += n` directly would be erased seconds later.
   *
   * Because it is rebuilt rather than accumulated, this is also the one capacity
   * key an **item** may safely name. `willMax` may not — it has a second home on
   * `World.willMax` that an unequip cannot take back, and there is a guard in
   * `items.ts` saying so. Mana has no second home: take the ring off and the
   * next `recomputeManaCap` simply computes a smaller number.
   *
   * Three of the owner's four Mana sources arrive here — research and item
   * passives write to it, and the leader's level is folded in by
   * `recomputeManaCap`. The fourth, unique buildings, is `BuildingType.mana`.
   */
  manaMax: number;
  /**
   * Better than nothing: the Hearth survives one killing blow per Siege at 1
   * health. A count, not a chance — 0 is off, 1 is the Coin of the Ferry.
   *
   * This used to be folded into `secondWind`, which meant Coin of the Ferry
   * quietly granted a weak company revival and never did the one thing its
   * card promised.
   */
  hearthSave: number;
}

export function defaultMods(): Mods {
  return {
    unitDamage: 1,
    unitHp: 1,
    moveSpeed: 1,
    attackSpeed: 1,
    wallHp: 1,
    towerDamage: 1,
    towerRange: 1,
    buildSpeed: 1,
    buildCost: 1,
    haulPenalty: 1,
    storage: 0,
    willRegen: 1,
    willMax: 1,
    powerDamage: 1,
    powerCooldown: 1,
    powerRadius: 1,
    soulGain: 1,
    resourceRate: 1,
    grainUpkeep: 1,
    popCap: 1,
    /** Additive, in tiles. */
    archerRange: 0,
    cavalryCharge: 1,
    veterancyRate: 1,
    thorns: 0,
    lifesteal: 0,
    siegeResist: 1,
    moraleFloor: 0,
    reinforceCost: 1,
    corpseRise: 0,
    willOnWaveEnd: 0,
    secondWind: 0,
    hearthSave: 0,
    manaMax: 0,
  };
}

export const ZoneKind = {
  /** A telegraphed impact that has not landed yet. */
  Strike: 0,
  Poison: 1,
  Heal: 2,
  Slow: 3,
  Fire: 4,
  Sunder: 5,
  Fear: 6,
  Rally: 7,
  Shield: 8,
} as const;

export interface Zone {
  kind: number;
  x: number;
  y: number;
  r: number;
  /** Seconds remaining. */
  t: number;
  /** Total lifetime, so the renderer can draw a wipe. */
  life: number;
  /** Damage per tick, heal rate, or telegraph payload depending on kind. */
  power: number;
  team: number;
  /** Whether this hurts everyone standing in it, including your own. */
  friendlyFire: boolean;
  /** Power id, for FX selection. */
  src: string;
}

/**
 * The character sheet.
 *
 * A hero is picked once, at the start of a run, and then *grows*: experience
 * from the things the simulation already counts (kills, waves held, buildings
 * raised), levels, skill points spent on a small tree, passives folded into
 * `mods` exactly the way relics and tech fold into it, and battle skills fired
 * from wherever the hero happens to be standing.
 *
 * Every field here is simulation state — it is hashed, saved and replayed. The
 * cooldowns in particular: a cooldown is not a cache, it is the reason the next
 * thing did or did not happen.
 */
export interface HeroState {
  /** '' until a hero is chosen. Otherwise a `HeroClass` id. */
  classId: string;
  /** Generated from the run's seed, so a replay names the same person. */
  name: string;
  xp: number;
  level: number;
  /** Skill ids bought, in the order they were bought. */
  skills: string[];
  /** Battle-skill cooldowns in seconds, keyed by the power the skill grants. */
  cooldowns: Record<string, number>;
  /** Handle of the hero's model on the field, or -1 while he is down. */
  unitH: number;
  /** The one-model company he leads, so he answers the same orders as anyone. */
  companyId: number;
  fallen: boolean;
  /** Seconds before the fallen can be called back. */
  reviveT: number;
}

export function emptyHero(): HeroState {
  return {
    classId: '',
    name: '',
    xp: 0,
    level: 1,
    skills: [],
    cooldowns: {},
    unitH: -1,
    companyId: -1,
    fallen: false,
    reviveT: 0,
  };
}

/**
 * One thing on the cart.
 *
 * `uid` rather than an index, because commands name items and an index into an
 * array that gets spliced is a different item by the time the command lands.
 * The id names the catalogue entry; the level is everything the player has
 * poured into it since, and is the reason a wave-four find is still worth
 * carrying at wave ninety.
 */
export interface ItemStack {
  /** Unique within the run. How a command and the UI name one particular item. */
  uid: number;
  /** Id into `src/data/items.ts`. */
  defId: string;
  /** 1 and upward, with no ceiling but the price of the next one. */
  level: number;
}

/** Five empty slots. A helper rather than a literal, so the count lives once. */
export function emptyEquipped(): number[] {
  return new Array<number>(ITEM_SLOT_COUNT).fill(-1);
}

export interface SpawnOrder {
  typeIdx: number;
  count: number;
  /** Index into `map.gates`. */
  gate: number;
  /** Seconds until this group walks on. */
  t: number;
  boss: boolean;
  /** 0 = march on the Hearth, 1 = raid the economy. */
  flowKind: number;
}

/**
 * A Chronicle event that has been drawn and not yet answered.
 *
 * This is state, not a UI flag. The simulation is *frozen* while it is set —
 * `stepWorld` returns early — so if it were not hashed and saved, a world that
 * had drawn an event and a world that had not would tick differently while
 * hashing the same, which is the exact shape of a silent desync. It is hashed
 * in `hash.ts` and written by `save.ts` for that reason.
 */
export interface PendingEvent {
  /** Id into the deck in `src/data/events.ts`. */
  id: string;
  /**
   * The choice ids actually offered, frozen at draw time. The offer must not be
   * recomputed at resolve time — the answer has to mean what the player read.
   */
  choices: string[];
  /** The wave it was drawn at. Part of the modal's identity for the UI. */
  wave: number;
}

export interface RunStats {
  kills: number;
  losses: number;
  wavesSurvived: number;
  resourcesProduced: ResourceBundle;
  powersCast: number;
  buildingsBuilt: number;
  buildingsLost: number;
  wallsLost: number;
  peakPop: number;
  damageDealt: number;
  damageTaken: number;
  /** Kills per unit type index, for the balance harness histogram. */
  killsByType: Record<number, number>;
  /** Every unit ever spawned, both sides. */
  usedByType: Record<number, number>;
  /** Only the units the player raised — this is what "bot builds" means. */
  playerUsedByType: Record<number, number>;
}

export interface WorldConfig {
  seed: number;
  factionId: string;
  difficultyId: string;
  /** Ash difficulty tuning: how long the first build phase lasts. */
  sandbox: boolean;
  /**
   * This world is a campaign's capital rather than a standalone run.
   *
   * The difference is who decides when a siege happens. A standalone run has a
   * build timer and a director that plans the next wave against you; a capital
   * is besieged when — and only when — a host actually arrives on the strategic
   * map. So the timer never runs out here and the director never plans, but
   * everything else about the settlement is identical, which is what lets the
   * same city be played either way.
   */
  homeCity?: boolean;
  /** Preset campaign village; its production belongs to the realm's monthly economy. */
  villageType?: VillageType | '';
  /**
   * Who these people are. Optional, and it falls back to whichever race plays as
   * `factionId` — so every existing call site, test and replay keeps working
   * untouched and gets exactly the race it always implicitly had.
   */
  raceId?: string;
  /** Which house of that people. Optional; '' means the race's default. */
  leaderId?: string;
  /**
   * The family's permanent unlocks, by id — see `src/data/bloodline.ts`.
   *
   * Travels in the config for the same reason the kingdom does: it must be
   * folded before the first tick, and a config is saved verbatim, so a loaded
   * or replayed run carries exactly the bloodline it was started with. The
   * simulation never reads the bank; the UI reads it once, here, at creation.
   * Unknown ids are dropped at resolve time rather than refused — a save from
   * a newer catalogue loads as a run without the unlock this build lacks.
   */
  bloodline?: string[];
  /**
   * The country this settlement stands in, as a `ProvinceTerrain` id.
   *
   * The two halves of this game have always disagreed about where you are: the
   * strategic map knows Emberhold sits in ash plain and its neighbour is a
   * mountain province, and the settlement generated for either was the same
   * mixture of wood, rock and marsh. Walking into a hold in the Hoarfast and
   * finding the identical temperate valley is the sort of thing that makes two
   * layers feel like two games.
   *
   * A string rather than an enum import, because `sim/world.ts` may not depend
   * on `data/realm.ts` — that is the layering rule, and the map generator only
   * needs to switch on it. Optional, so every existing caller, save and replay
   * is untouched and gets the temperate baseline it always had.
   *
   * It travels in the config for the same reason `raceId` does: a config is
   * saved verbatim and folded before the first tick, so a loaded or replayed
   * run rebuilds exactly the ground it was created with.
   */
  biome?: string;
  /**
   * Which holding this settlement *is*, as a province index.
   *
   * A campaign used to have exactly one settlement world and it was the
   * capital, so nothing ever had to ask which town it was standing in. It does
   * now: a crown may hold several, and when the player walks out of one, the
   * development, the walls and the garrison works measured off it have to be
   * written back to the province it actually stands on rather than to the seat.
   * See `syncCapitalDevelopment`.
   *
   * On the config rather than on `World`, and unhashed, for the same reason
   * `homeCity` is — which is the closer precedent than it looks, because
   * `homeCity` genuinely forks the tick (`stepPhase` pins `phaseT` to Infinity
   * for it) and is still identity rather than state. Nothing inside `stepWorld`
   * reads this field; it says *which* world this is, the way `seed` and
   * `factionId` do, and a config is saved verbatim, so a loaded or replayed
   * hold is the same hold it was.
   *
   * Optional, so every existing caller, save and replay is untouched:
   * `undefined` means "the capital, or a run with no realm around it", which is
   * what every world written before this field was one.
   */
  province?: number;
}

export interface World {
  // ── Identity ──────────────────────────────────────────────────────────────
  readonly config: WorldConfig;
  readonly faction: FactionDef;
  readonly difficulty: DifficultyDef;

  // ── State (hashed, saved, replayed) ──────────────────────────────────────
  tick: number;
  rng: Rng;
  /** A separate stream for the director so build decisions don't perturb waves. */
  rngDirector: Rng;
  /**
   * A third stream, for the campaign's AI nations and abstract battle
   * resolution. Separate for the same reason the director's is: a change to how
   * a nation picks a target must not shift which wave arrives in a settlement.
   */
  rngCampaign: Rng;

  map: TileMap;
  units: UnitStore;
  buildings: BuildingStore;
  proj: ProjectileStore;
  /**
   * The trees, the rocks and the ore, as objects standing in the world.
   *
   * A fourth store, seeded from the terrain at `initRun` and worked down by
   * hauling workers until a node is empty, at which point it is removed and the
   * ground under it reverts to ash plain. Hashed and saved with the other three:
   * a wood that has been felled and a wood that has not are not the same map,
   * and the difference is permanent.
   *
   * See `src/sim/systems/nodes.ts` for the store and the index over it, and
   * `src/data/nodes.ts` for what a kind is worth and how thickly it stands.
   */
  nodes: ResourceNodeStore;

  /**
   * Who these people are, as opposed to whose banner they carry.
   *
   * A `RaceId` from `data/races.ts`. It decides the muster binding, the economy
   * mask, two passives, the fourth Academy branch and the bone the leader's
   * portrait is drawn on. It is **not** the faction: `w.faction` still points at
   * the `FactionDef` carrying the accent colours, the unit roster and the
   * architecture, and several races share one. See `raceOfFaction`.
   *
   * Hashed and saved. It changes what production, upkeep, recruitment, the
   * market and the storage clamp do, so two settlements identical in every other
   * field are not the same settlement if this differs.
   */
  raceId: string;
  /**
   * Which house of that people, and therefore which named character leads them.
   *
   * '' means the race's default house. A `Leader` id from `data/leaders.ts`; its
   * two passives are folded on top of the race's at world creation, and its
   * `archetype` names the existing hero class it plays as.
   */
  leaderId: string;

  res: ResourceBundle;
  resCap: number;
  phase: PhaseId;
  /** Seconds remaining in the current phase (build/spoils only). */
  phaseT: number;
  wave: number;
  age: number;
  /**
   * Which rung of the realm's ladder this settlement stands on, or 0 for a
   * world that is not a holding at all.
   *
   * State rather than config, and that is the whole of the difference between a
   * village and a small town: a village can be *built into* a town, and
   * `promoteVillages` is what does it. A config is identity and is written once;
   * this changes, so it is hashed and saved with everything else that does.
   *
   * 0 means "not a holding of a realm" — the capital, a skirmish, a standalone
   * siege, and every world written before this field existed. All of those are
   * full settlements with the whole catalogue, which is why 0 rather than 1 is
   * the default: a `rung` of 1 would silently turn every old save into a
   * village. See `isVillage` in `campaign/commonwealth.ts`.
   */
  rung: number;
  /** Last applied village preset level. Zero means uninitialised or a legacy layout. */
  villageLevel: number;
  /** Exact preset geometry, retained while structures are ruins. */
  villageBlueprint: {
    roofs: Array<{ type: number; x: number; y: number; tier: number; maxHp: number; level?: number; key?: string }>;
    walls: Array<{ tile: number; kind: number; tier: number; maxHp: number; level?: number }>;
  } | null;
  /** Campaign month last considered for automatic village recovery. */
  villageLastTurn: number;
  /**
   * The Age currently being researched, or 0 for none.
   *
   * An Age is bought and then waited for. The cost leaves the treasury the
   * moment this is set, so abandoning is not free — there is no command to
   * abandon, and that is deliberate.
   */
  /** The Hearth has already been saved from a killing blow this Siege. */
  hearthSaved: boolean;
  /**
   * Bought ranks per unit type id — the settlement getting better at making
   * soldiers, as opposed to the soldiers getting better at soldiering, which is
   * company veterancy and lives on `Company.rank`.
   *
   * Keyed by unit id rather than by type index so a save survives the roster
   * being reordered, and hashed by sorted key for the same reason
   * `powerCooldowns` is.
   */
  unitRanks: Record<string, number>;
  researchingAge: number;
  /** Seconds of research accrued toward `researchingAge`. */
  ageResearch: number;

  /**
   * The Long Ledger: whole days since the run began, and the seconds accrued
   * into the current day. Month, season and year are derived from the day count
   * rather than stored, so they cannot drift from it — see
   * `src/sim/systems/calendar.ts`, which also states why a day is forty seconds
   * of simulated time and not a wave or a campaign turn.
   */
  calendarDay: number;
  calendarT: number;

  /**
   * This world is held because the player is away fighting a battle by hand.
   *
   * The realm and the settlement both run in real time now, which makes "what
   * stops them" a design decision rather than an implementation detail. Exactly
   * one thing does: a tactical battle. While it is set, `stepWorld` returns
   * after advancing the tick counter and nothing else happens — no
   * construction, no production, no marches, no council.
   *
   * State rather than a frame-loop flag, for the reason the Chronicle's pause is
   * state: a pause whose length depends on how long a player took to win a fight
   * is a pause two replays of the same command log will disagree about. Set and
   * cleared through `Cmd.SetBattleHold`, hashed, and saved.
   */
  battleHold: boolean;

  pop: number;
  popCap: number;
  /**
   * The standing pool, and how much of it the host is standing in.
   *
   * The owner's own example of a structural race difference, and the one that is
   * built: the unburied do not pay for troops with houses. `mana` is what the
   * host currently occupies and `manaMax` is the capacity, exactly parallel to
   * `pop` and `popCap` — `mana` is recomputed each tick in `stepPopulation` off
   * the same loop that counts heads, and `manaMax` is rebuilt by
   * `recomputeManaCap` whenever the building stock changes.
   *
   * Both hashed and saved. `mana` looks derived and is hashed anyway for the
   * same reason `pop` is: it is recomputed from live units, so a world that
   * hashes it and one that does not are indistinguishable right up until
   * somebody loads a save and gets a different answer for a tick.
   *
   * Every race carries these fields and exactly one reads them — only a race
   * whose `binding` is `'mana'` has `recruitCompany` look. That is deliberate: a
   * relic or Chronicle card granting Mana to somebody who cannot spend it is a
   * no-op rather than a crash, and it costs one branch and no extra state.
   */
  mana: number;
  manaMax: number;

  /**
   * The Gilded Vigil's reliquary: names made, and the scribe's progress toward
   * the next one.
   *
   * The `names` binding, and it is a *production line* rather than a pool —
   * `consecratedNames` is how many finished names are on the shelf, capped by
   * `nameVaultCap`, and `nameWork` is seconds of the one scribe's time accrued
   * toward the next. A company spends a name when it musters and takes it to the
   * grave with it; a Vigil that loses a line loses a week, which is a currency
   * no amount of gold converts into.
   *
   * Both hashed and saved. `nameWork` looks like an accumulator nobody would
   * miss and is the reason a save mid-consecration reloads mid-consecration
   * rather than starting the week again — the same argument `calendarT` makes.
   *
   * Every race carries these and one reads them, exactly as `mana` is carried by
   * every race and read by the Litany. Opening at one name rather than zero
   * because a people that cannot field its first company until ninety seconds
   * have passed is a people whose first build phase is a loading screen.
   */
  consecratedNames: number;
  nameWork: number;

  /**
   * The Drowned Court's pens: heads carried off and not yet spent.
   *
   * The `thralls` binding. Filled by the capture path in `damageUnit` — an
   * enemy broken below `thrallThreshold` on your ground leaves the field as
   * property rather than as a corpse — and emptied a head at a time by
   * `recruitCompany`. It is a stock and not a capacity, which is what makes the
   * Court the one people whose army shrinks when it is winning quietly: nothing
   * produces a thrall but a fight.
   *
   * Hashed and saved. Carried by every race and read by one, as above.
   */
  thralls: number;
  /**
   * The age bands: heads not yet grown, heads past working, and the fraction
   * of a death the grave is owed. Adults are **derived** — `pop − children −
   * elders` — and deliberately not stored, for the reason the calendar does
   * not store the month: a count that cannot exist cannot disagree with the
   * two it was supposed to be the remainder of.
   *
   * Bands, not per-model ages, and the rejection is argued at the top of
   * `src/data/demography.ts`: an age column would be a hot-loop byte per soul
   * and a save-format hazard, answering a question the simulation never asks.
   * Stepped monthly-by-rate in `stepDemography` (`settlement.ts`), read by
   * `assignJobs` (only adults work) and `musterRoom` (only adults muster),
   * charged by `killUnit` (a death draws on the bands proportionally).
   *
   * All three hashed and saved. `grave` looks like an accumulator nobody would
   * miss and is the reason a save taken half a death before the next funeral
   * reloads half a death before it — the same argument `calendarT` and
   * `nameWork` make. Bounded (see `stepDemography`), so it cannot drift a
   * float in hashed state the way the Hollow's toll note warns about.
   */
  demography: { children: number; elders: number; grave: number };
  morale: number;
  /** Index into TAX_BANDS. The one economic dial the player holds directly. */
  taxPolicy: number;
  /**
   * Seconds the treasury has been unable to meet the wage bill. Soldiers
   * tolerate a late purse for a while and then start walking home.
   */
  arrears: number;
  /** 0..1 — how much of the settlement's appetite was actually met last tick. */
  fedFraction: number;
  /** 0..1 — how much of that was bread rather than raw grain. */
  breadShare: number;
  /**
   * 0..1 — how varied last tick's diet was: 0 for a town eating one thing,
   * 1 for a town eating all four.
   *
   * State rather than a readout, because `moraleTarget` and the Hearth's own
   * growth rate both read it, so whether it was computed decides what happens
   * next — which is the rule for the difference between a cache and a fact.
   * Hashed beside `breadShare` and saved with it.
   */
  dietVariety: number;
  /** Uncleared rubble tiles. Kept as a counter so idle villagers can skip the scan. */
  rubbleCount: number;
  /**
   * Buildings the player has explicitly ordered repaired, in the order asked.
   *
   * Damaged buildings have always been mended by an idle gang between waves, on
   * a slot-order sweep with a budget on it — which is fine for a town at peace
   * and useless the moment the player has an opinion about *which* wall goes
   * back up first. An order does two things nothing else can: it puts the crew
   * on that building **before** the sweep and at twice the size, and it is the
   * one thing that repairs **during a Siege**, when the automatic sweep is
   * deliberately switched off ("nobody re-points masonry with a ram at the
   * gate"). Emergency repairs cost the same materials as any other, so it is a
   * decision rather than a free button.
   *
   * State, not a UI list: it changes where hands go and what gets mended, so it
   * is hashed and saved. Pruned in `assignJobs` the moment a building is dead or
   * whole again, which is what stops it growing without bound.
   */
  repairOrders: number[];
  will: number;
  willMax: number;

  companies: Company[];
  nextCompanyId: number;

  hearth: number;
  /**
   * Whose stonework and roofs the ground carries.
   *
   * A wall tile has no owner field — `map.wall` is a byte — so for the whole
   * life of this project "whose wall is this" was answered with the constant
   * `Team.Player`, in five separate files. That is true of every settlement and
   * it is *false* of the one place the owner actually met a wall circuit: an
   * assault on somebody else's town, where the walls belong to the defender and
   * the player is the one outside them. With the constant in place the attackers
   * were hostile to nothing on the map, so they would not so much as swing at
   * the stone, the defender's gates stood open to them alone, and they pressed
   * against the circuit for ever. That is the wedge, and it is one fact stated
   * wrongly rather than five bugs.
   *
   * A *team*, read through `alliedWith` / `areHostile` like every other team
   * question, so a second banner on the wall owner's side owns the same walls.
   * `Team.Player` everywhere except an assault field, which is exactly what the
   * constant used to mean — so every settlement, save and replay is untouched.
   */
  wallTeam: number;
  gameOver: boolean;
  causeOfDeath: string;
  victoryWave: number;

  /** Player-selected attack vectors for the coming wave, filled by the director. */
  incomingGates: number[];
  incomingFaction: string;
  incomingComposition: Array<{ typeIdx: number; count: number }>;
  incomingModifiers: string[];
  incomingBudget: number;
  /**
   * The guided run: which of the five lessons this wave is bent for.
   *
   * 0 on every ordinary run, 1–5 during the guided waves, 6 once they are
   * behind it. It changes what the director plans — see
   * `systems/tutorial.ts` — so it is simulation state: hashed, saved, and
   * written only through `Cmd.TutorialBegin` / `Cmd.TutorialSkip` and the
   * one advance at the end of a siege.
   */
  tutorialStep: number;
  /** Staggered arrivals for the current siege, drained by the director. */
  spawnQueue: SpawnOrder[];

  /**
   * Set only in Battle mode. Its presence is what switches the tick from
   * "settlement under siege" to "two armies on a field".
   */
  battle?: BattleState;
  /**
   * Set only on the realm world. Its presence means "this world is the
   * strategic map": it has no Hearth and no villagers, so `stepWorld` skips the
   * settlement systems for it and runs `stepRealm` instead.
   *
   * It no longer means "and therefore nothing runs on the clock". The realm
   * advances on the same Long Ledger the settlement does — one resolution a
   * month — and the city that stands in it is a second `World`, stepped
   * alongside this one by `stepGame`.
   */
  campaign?: CampaignState;
  /**
   * The crown's other towns, as one purse. Set on every settlement world of a
   * campaign by `linkCrown` at the top of `stepGame`; absent on a skirmish, a
   * standalone siege and the realm itself.
   *
   * **Topology, not state — deliberately unhashed and unsaved.** It says which
   * worlds belong to the same crown, the way `config.province` says which world
   * this is, and every number it moves is moved out of a `res` bundle that is
   * hashed and saved already. See `campaign/commonwealth.ts` for the argument
   * in full and for what goes wrong if it is hashed.
   */
  purse?: CrownPurse;
  /** Rolling record of the player's defensive profile, read by the director. */
  profile: DefenceProfile;

  /** Ground effects: meteors in flight, poison clouds, sanctified ground. */
  zones: Zone[];

  unlockedBuildings: string[];
  unlockedPowers: string[];
  powerCooldowns: Record<string, number>;
  powerTiers: Record<string, number>;
  relics: string[];
  techNodes: string[];
  /**
   * The effective modifier bag. **Derived from `modsBase` plus what is
   * equipped**, and every system in the game reads it — which is why it stayed
   * the field with the short name.
   */
  mods: Mods;
  /**
   * The bag as it stands with nothing equipped.
   *
   * This is the answer to the one trap the item system sets. Relics, tech nodes,
   * Chronicle answers and hero passives *fold* into the bag and never come out
   * again; equipment comes out again all the time. Folding a multiplier in on
   * equip and dividing it out on unequip accumulates float error at a rate that
   * a long run will absolutely notice, and a replay that diverges by one ulp of
   * `unitDamage` diverges completely a hundred ticks later.
   *
   * So nothing is ever divided out. `modsBase` holds everything permanent, the
   * equipment is folded on top of a *fresh copy* of it every time the kit
   * changes, and unequipping is a re-fold of a smaller set rather than the
   * inverse of a multiplication. The same set of items always produces the same
   * bits, because it is always the same sequence of multiplications applied to
   * the same base, in slot order. See `refoldEquipment` in
   * `src/sim/systems/items.ts` and `foldPermanent` in `src/sim/apply.ts`.
   */
  modsBase: Mods;
  /** Relic choices offered after the current Siege, by relic id. */
  relicOffer: string[];
  /** The character this run is about. `classId === ''` means no hero was picked. */
  hero: HeroState;
  companions: CompanionHero[];
  nextCompanionId: number;

  // ── The cart ─────────────────────────────────────────────────────────────
  /**
   * Everything found and not yet melted down, equipped or otherwise. Equipped
   * items stay in this list; `equipped` points at them. One list means an item
   * cannot be in two places, which is the bug the two-list version invites.
   */
  inventory: ItemStack[];
  /** `ItemStack.uid` per slot, or −1. Indexed by `ItemSlot`. */
  equipped: number[];
  /** The uid the next find will be given. Monotonic; never reused. */
  nextItemUid: number;
  /**
   * A fourth RNG stream, for what falls out of a won field.
   *
   * Separate for exactly the reason `rngDirector` and `rngEvent` are separate:
   * a change to the drop table must not shift which wave arrives next or which
   * card the Chronicle draws. Its cursor is hashed and saved.
   */
  rngSpoils: Rng;

  // ── The Chronicle ────────────────────────────────────────────────────────
  /**
   * A third RNG stream, for exactly the reason `rngDirector` is a second one:
   * drawing an event must not shift the wave the director is about to plan.
   * Its cursor is hashed — without that, two worlds that had drawn a different
   * number of events would hash identically and determinism would die quietly.
   */
  rngEvent: Rng;
  /** The undecided event, or null. While it is set the simulation is frozen. */
  pendingEvent: PendingEvent | null;
  /** Answers so far, as `eventId>choiceId`. Keeps each event to once a run. */
  eventHistory: string[];
  /** The wave the last event was drawn on. −99 means "none yet this run". */
  lastEventWave: number;

  // ── Derived (rebuilt each tick, never hashed) ────────────────────────────
  /**
   * Company id → index into `companies`. A pure accelerator for `findCompany`;
   * see the note there for why it is safe to leave out of the hash and the save.
   */
  companyIdx: Map<number, number>;
  /**
   * The battlefield scatter, as one byte a tile — see `src/sim/obstacles.ts`.
   *
   * Derived from `battle.obstacles`, which is the state; this is the cache, and
   * it is neither hashed nor saved for the same reason the flow fields are not.
   * `null` in every world with no scatter in it, which is every settlement and
   * every campaign, so the collision pass and the Dijkstra pay a null check
   * rather than an allocation they have no use for.
   */
  obstacleGrid: Uint8Array | null;
  /**
   * The battlefield's relief, as one byte a tile — see `src/sim/field.ts`.
   *
   * The same split as `obstacleGrid` above, one line down and for one reason:
   * the state is `battle.hills`, a handful of discs, and this is the cache
   * derived from it. Neither hashed nor saved; rebuilt at `createBattle` and
   * once on load. `null` on every world with no relief, which is every
   * settlement, and that null is what keeps the movement pass paying a single
   * null check rather than a division per model per tick.
   *
   * The unit is 1/`HEIGHT_UNITS` of a tile, so the whole byte is a shade under
   * eight tiles of climb. Read it through `fieldHeight`, never directly.
   */
  heightGrid: Uint8Array | null;
  spatial: SpatialHash;
  /** Invader route to the Hearth. Most of the horde walks this. */
  flowMain: FlowField;
  /** Invader route to the outlying economy — what raider squads follow. */
  flowEcon: FlowField;
  /** Defender route home, used by villagers and by routing companies. */
  flowHome: FlowField;
  /** Cached A* routes to work sites, shared by everyone headed the same way. */
  paths: PathCache;
  /** Per-building placement multipliers, rebuilt whenever the layout changes. */
  districts: DistrictCache;
  events: SimEvent[];
  /** Cheap per-tick aggregates the UI and AI both read. */
  counts: {
    player: number;
    enemy: number;
    villagers: number;
    idleVillagers: number;
  };
  /**
   * Last tick's money flow, in gold per minute, for the HUD. Pure functions of
   * hashed state, recomputed every tick — derived, so deliberately unhashed.
   */
  econ: {
    taxIncome: number;
    tradeIncome: number;
    wageBill: number;
    /** taxIncome + tradeIncome - wageBill. The number the player actually wants. */
    net: number;
    /** Models currently drawing a wage. */
    onPayroll: number;
  };
  dirty: {
    flow: boolean;
    /**
     * A wall has taken damage since the horde last re-read the approach.
     *
     * Distinct from `flow`, which means the navigable world changed *shape*.
     * This one means it changed *price*: walls are passable at a cost that
     * scales with their remaining health, so a section being chewed on makes
     * the horde re-weight where it piles in. That is what the siege heartbeat
     * is for — and without this flag the heartbeat fired every three seconds of
     * every siege regardless, which on a 384×384 map is a hundred and fifty
     * thousand tiles of Dijkstra spent to arrive at the field we already had.
     */
    flowWeights: boolean;
    jobs: boolean;
    districts: boolean;
  };
}

export interface DefenceProfile {
  /** Rolling means over the last few waves, each 0..1. */
  archerRatio: number;
  meleeMass: number;
  wallIntegrity: number;
  chokeReliance: number;
  spellUse: number;
  economyExposure: number;
  samples: number;
}

export function emptyProfile(): DefenceProfile {
  return {
    archerRatio: 0.25,
    meleeMass: 0.5,
    wallIntegrity: 0,
    chokeReliance: 0,
    spellUse: 0,
    economyExposure: 0.3,
    samples: 0,
  };
}

export function emptyStats(): RunStats {
  return {
    kills: 0,
    losses: 0,
    wavesSurvived: 0,
    resourcesProduced: emptyResources(),
    powersCast: 0,
    buildingsBuilt: 0,
    buildingsLost: 0,
    wallsLost: 0,
    peakPop: 0,
    damageDealt: 0,
    damageTaken: 0,
    killsByType: {},
    usedByType: {},
    playerUsedByType: {},
  };
}

/** Mutable run statistics live outside `World` so hashing stays cheap. */
export const worldStats = new WeakMap<World, RunStats>();

export function statsOf(w: World): RunStats {
  let s = worldStats.get(w);
  if (!s) {
    s = emptyStats();
    worldStats.set(w, s);
  }
  return s;
}

export function createWorld(config: WorldConfig): World {
  const fac = faction(config.factionId);
  const diff = difficulty(config.difficultyId);

  const w: World = {
    config,
    faction: fac,
    difficulty: diff,

    tick: 0,
    rng: makeRng(deriveSeed(config.seed, 1)),
    rngDirector: makeRng(deriveSeed(config.seed, 977)),
    rngCampaign: makeRng(deriveSeed(config.seed, 613)),

    map: new TileMap(),
    units: new UnitStore(),
    buildings: new BuildingStore(),
    proj: new ProjectileStore(),
    nodes: new ResourceNodeStore(),

    raceId: config.raceId ?? raceOfFaction(config.factionId),
    leaderId: config.leaderId ?? '',

    res: emptyResources(),
    resCap: 500,
    phase: Phase.Build,
    phaseT: 0,
    wave: 0,
    age: 1,
    rung: 0,
    villageLevel: 0,
    villageBlueprint: null,
    villageLastTurn: 0,
    hearthSaved: false,
    unitRanks: {},
    researchingAge: 0,
    ageResearch: 0,
    // Day zero is the first of Mudmonth, Year 1: a run opens in the thaw, with
    // two seasons of grace before the first Hardfrost.
    calendarDay: 0,
    calendarT: 0,
    battleHold: false,

    pop: 0,
    popCap: 0,
    mana: 0,
    manaMax: 0,
    // One name, said at the founding. See the field note for why it is not zero.
    consecratedNames: 1,
    nameWork: 0,
    thralls: 0,
    demography: { children: 0, elders: 0, grave: 0 },
    morale: 0.7,
    taxPolicy: TaxPolicy.Fair,
    arrears: 0,
    fedFraction: 1,
    breadShare: 0,
    dietVariety: 0,
    rubbleCount: 0,
    repairOrders: [],
    will: 40,
    willMax: 100,

    companies: [],
    nextCompanyId: 1,

    hearth: -1,
    wallTeam: Team.Player,
    gameOver: false,
    causeOfDeath: '',
    victoryWave: 0,

    incomingGates: [],
    incomingFaction: 'ironfang',
    incomingComposition: [],
    incomingModifiers: [],
    incomingBudget: 0,
    tutorialStep: 0,
    spawnQueue: [],
    zones: [],
    profile: emptyProfile(),

    unlockedBuildings: [],
    unlockedPowers: [],
    powerCooldowns: {},
    powerTiers: {},
    relics: [],
    techNodes: [],
    mods: defaultMods(),
    modsBase: defaultMods(),
    relicOffer: [],
    hero: emptyHero(),
    companions: [],
    nextCompanionId: 1,

    inventory: [],
    equipped: emptyEquipped(),
    nextItemUid: 1,
    rngSpoils: makeRng(deriveSeed(config.seed, 7717)),

    rngEvent: makeRng(deriveSeed(config.seed, 4441)),
    pendingEvent: null,
    eventHistory: [],
    lastEventWave: -99,

    companyIdx: new Map(),
    obstacleGrid: null,
    heightGrid: null,
    spatial: new SpatialHash(),
    flowMain: new FlowField(),
    flowEcon: new FlowField(),
    flowHome: new FlowField(),
    paths: new PathCache(),
    districts: new DistrictCache(),
    events: [],
    counts: { player: 0, enemy: 0, villagers: 0, idleVillagers: 0 },
    econ: { taxIncome: 0, tradeIncome: 0, wageBill: 0, net: 0, onPayroll: 0 },
    dirty: { flow: true, flowWeights: false, jobs: true, districts: true },
  };

  worldStats.set(w, emptyStats());
  w.map.resourceSolid = w.nodes.solidTiles;
  return w;
}

/** Place the Hearth, the starting villagers and the starting stockpile. */
export function initRun(w: World): void {
  const mapRng = makeRng(deriveSeed(w.config.seed, 31));
  generateMap(w.map, mapRng, w.config.biome);
  /**
   * And then the wood is trees.
   *
   * Immediately after the terrain pass and before anything is stamped on it, so
   * the nodes stand exactly where the generator painted the wealth — the brief's
   * "a map still looks like the map it was". It consumes no draw from `mapRng`
   * (see `seedResourceNodes`), so every map this build generates is tile-for-tile
   * the map the last build generated.
   */
  seedResourceNodes(w.map, w.nodes);

  // Who these people are, before anything is computed from it. The two race
  // passives and then the two of the house, in that order and never the other
  // one — see `foldIdentity`. The bloodline folds third, always: it is what
  // the family carries *into* an identity, so it layers on top of whoever
  // they are this run, and the order is fixed for the same floating-point
  // reason as the other two.
  foldIdentity(w);
  foldBloodline(w);

  // The Hearth.
  const hearthDef = buildingType(HEARTH_TYPE);
  const hx = w.map.hearthX - ((hearthDef.w / 2) | 0);
  const hy = w.map.hearthY - ((hearthDef.h / 2) | 0);
  const h = placeBuilding(w, HEARTH_TYPE, hx, hy, Team.Player, true);
  w.hearth = h;

  const startMul = w.difficulty.startMul;
  addResources(
    w.res,
    { timber: 300, planks: 40, stone: 200, iron: 60, grain: 240, bread: 40, gold: 220, souls: 0 },
    startMul,
  );
  // The bloodline's heirlooms, deliberately unscaled by difficulty: the
  // strongbox holds what it holds, whichever night it is dug up in.
  addResources(w.res, bloodlineStart(w.config.bloodline));

  // Opening villagers, scattered around the Hearth.
  for (let i = 0; i < 12; i++) {
    const ang = (i / 12) * Math.PI * 2;
    const r = 3.2 + (i % 3) * 0.7;
    spawnUnit(
      w,
      VILLAGER_TYPE,
      Team.Player,
      w.map.hearthX + 0.5 + Math.cos(ang) * r,
      w.map.hearthY + 0.5 + Math.sin(ang) * r,
    );
  }
  // All twelve grown — the opening balance, argued at `seedDemography`. The
  // head counts are set here as well rather than left for the first tick:
  // `musterRoom`'s adult gate reads `counts.villagers` the way its bed test
  // has always read `pop`, and a muster ordered on tick zero should be judged
  // against the town that is actually standing there.
  seedDemography(w);
  w.pop = 12;
  w.counts.villagers = 12;

  /**
   * What the ships carried up the water stairs, and why the Court gets an
   * opening stock when nobody else's binding does.
   *
   * A thrall exists only because somebody was beaten for it, so a Drowned Court
   * that starts at zero starts **deadlocked**: it cannot field a company, so it
   * cannot hurt anybody, so it can never take a captive, so it can never field a
   * company. The harness found this in a seven-minute run that raised nothing at
   * all — which is exactly the shape of failure a binding is most likely to have
   * and least likely to be noticed for, because from the outside it reads as a
   * quiet settlement rather than as a rule with no base case.
   *
   * Two companies' worth, so the opening is a *decision* — spend them on one
   * good company or two thin ones — and the second wave has to pay for itself.
   */
  if (race(w.raceId).binding === 'thralls') w.thralls = OPENING_THRALLS;
  /**
   * And the same float for the same reason, for the other people who pay in
   * something taken rather than something grown. See `OPENING_SOULS`: without
   * it the Litany opens unable to raise a single company on its entire roster,
   * and measurably never raises one at all.
   *
   * Unscaled by `startMul`, like the bloodline's heirlooms above and unlike
   * everything in the grant before it — this is not a stock of goods to be
   * generous or stingy with, it is the base case of a rule, and a rule with no
   * base case on the hardest tier is a faction that cannot be played there.
   */
  if (race(w.raceId).binding === 'mana') w.res.souls += OPENING_SOULS;

  recomputePopCap(w);
  recomputeStorage(w);
  recomputeManaCap(w);

  // The Sovereign's opening kit, plus whatever the Hearth itself grants,
  // plus whatever the family has kept.
  w.unlockedPowers = [...STARTING_POWERS];
  for (const g of buildingType(HEARTH_TYPE).grants) {
    if (!w.unlockedPowers.includes(g)) w.unlockedPowers.push(g);
  }
  for (const g of bloodlinePowers(w.config.bloodline)) {
    if (!w.unlockedPowers.includes(g)) w.unlockedPowers.push(g);
  }
  for (const p of w.unlockedPowers) w.powerCooldowns[p] = 0;

  w.phase = Phase.Build;
  w.phaseT = FIRST_BUILD_PHASE_SECONDS * w.difficulty.buildTimeMul;
  w.wave = 0;
  w.dirty.flow = true;
  w.dirty.jobs = true;
}

// ── Entity helpers ─────────────────────────────────────────────────────────

export function spawnUnit(w: World, typeIdx: number, team: number, x: number, y: number): number {
  const i = w.units.spawn();
  if (i < 0) return -1;
  const t = unitType(typeIdx);
  const u = w.units;
  const bonus = team === Team.Player ? w.faction.bonuses : undefined;

  u.typeId[i] = typeIdx;
  u.team[i] = team;
  u.faction[i] = factionIndex(t.faction === 'common' ? w.faction.id : t.faction);
  u.x[i] = x;
  u.y[i] = y;
  /**
   * Never bring anyone into the world inside a wall or a footprint.
   *
   * The settler ring around the Hearth is 2.4 tiles, and nothing stops the
   * player building an arrow tower exactly there — the harness caught two
   * villagers *born* inside one, frozen byte-identical from their first tick,
   * because the collision pass can refuse a step into stone but can never
   * undo one. The check is against this unit's own exemptions (`tileBlocks`),
   * so a spawn onto open ground — every battle muster, every gate — pays one
   * tile test and moves nothing.
   */
  if ((t.flags & UF.Flying) === 0 && inMap(Math.floor(x), Math.floor(y)) && tileBlocks(w, Math.floor(x), Math.floor(y), i)) {
    const spot = nearestOpenTile(w, Math.floor(x), Math.floor(y), i, x, y);
    if (spot) {
      u.x[i] = spot.x + 0.5;
      u.y[i] = spot.y + 0.5;
    }
  }
  u.goalX[i] = u.x[i];
  u.goalY[i] = u.y[i];
  const isPlayer = team === Team.Player;
  u.maxHp[i] = t.hp * (bonus?.unitHp ?? 1) * (isPlayer ? w.mods.unitHp * rankHpMul(w.unitRanks[t.id] ?? 0) : 1);
  u.hp[i] = u.maxHp[i];
  u.radius[i] = t.radius;
  u.speed[i] = t.speed * (bonus?.moveSpeed ?? 1) * (isPlayer ? w.mods.moveSpeed : 1);
  u.flags[i] = t.flags;
  u.morale[i] = 1;
  u.state[i] = UnitState.Idle;
  u.facing[i] = 0;
  u.job[i] = Job.Idle;
  u.ammo[i] = t.ammo;
  u.fatigue[i] = 0;

  const stats = statsOf(w);
  stats.usedByType[typeIdx] = (stats.usedByType[typeIdx] ?? 0) + 1;
  if (isPlayer) stats.playerUsedByType[typeIdx] = (stats.playerUsedByType[typeIdx] ?? 0) + 1;
  return i;
}

/**
 * Stamp a building onto the map. `instant` skips construction (used for the
 * Hearth and for debug spawns).
 */
export function placeBuilding(
  w: World,
  typeIdx: number,
  tx: number,
  ty: number,
  team: number,
  instant = false,
): number {
  const def = buildingType(typeIdx);
  const i = w.buildings.spawn();
  if (i < 0) return -1;
  const b = w.buildings;
  b.typeId[i] = typeIdx;
  b.tx[i] = tx;
  b.ty[i] = ty;
  b.team[i] = team;
  b.maxHp[i] = def.hp;
  b.hp[i] = instant ? def.hp : Math.max(1, def.hp * 0.25);
  b.build[i] = instant ? 1 : 0;
  b.tier[i] = 1;

  for (let dy = 0; dy < def.h; dy++) {
    for (let dx = 0; dx < def.w; dx++) {
      const x = tx + dx;
      const y = ty + dy;
      if (!w.map.inBounds(x, y)) continue;
      w.map.building[w.map.idx(x, y)] = i;
    }
  }
  // Direct world builders can clear a plot; player placement separately keeps
  // resource camps beside their sources and all buildings off mineral faces.
  clearNodesInRect(w.nodes, tx, ty, tx + def.w - 1, ty + def.h - 1);
  // Whoever was standing on the footprint steps off it, or they never move
  // again — see `evictFromRect`.
  evictFromRect(w, tx, ty, tx + def.w - 1, ty + def.h - 1);
  // Ground routes through gates also depend on the foundations beside them.
  w.map.wallChanged();
  w.dirty.flow = true;
  w.dirty.jobs = true;
  w.dirty.districts = true;
  return i;
}

/**
 * Take a building off the map. When it was *smashed* rather than demolished it
 * leaves rubble: the ground stays unusable until somebody clears it, and the
 * rubble remembers what stood there so rebuilding is one click rather than a
 * memory test. This is the rebuild half of the loop — a siege should cost you
 * a quarter of your town and an evening of putting it back.
 */
export function removeBuilding(w: World, i: number, leaveRubble = false): void {
  if (!w.buildings.isAlive(i)) return;
  const def = buildingType(w.buildings.typeId[i]);
  const tx = w.buildings.tx[i];
  const ty = w.buildings.ty[i];
  const typeMark = w.buildings.typeId[i] + 1;
  for (let dy = 0; dy < def.h; dy++) {
    for (let dx = 0; dx < def.w; dx++) {
      const x = tx + dx;
      const y = ty + dy;
      if (!w.map.inBounds(x, y)) continue;
      const k = w.map.idx(x, y);
      if (w.map.building[k] === i) w.map.building[k] = -1;
      if (leaveRubble && w.map.rubble[k] === 0) {
        w.map.rubble[k] = typeMark;
        // Rubble can appear part-way through a tick, and the map's rubble index
        // is only rebuilt once per tick. Say so rather than let a villager look
        // at a list that predates the collapse.
        w.map.invalidateRubbleIndex();
        w.map.rubbleWork[k] = RUBBLE_CLEAR_SECONDS;
        w.map.stain[k] = Math.min(255, w.map.stain[k] + 70);
        w.rubbleCount++;
      }
    }
  }
  // Unassign workers.
  const u = w.units;
  for (let k = 0; k < u.slots.high; k++) {
    if (u.slots.alive[k] === 0) continue;
    if (u.workSite[k] === i) {
      u.workSite[k] = -1;
      u.job[k] = Job.Idle;
      u.state[k] = UnitState.Idle;
      // A hauler whose camp is rubble has nowhere to put the load down, and a
      // node he is still walking to is nobody's tree now. Both are dropped
      // rather than carried into an idle life — a villager standing in the
      // market holding four timber for the rest of the run is state that
      // nothing will ever collect.
      u.nodeTarget[k] = -1;
      u.carry[k] = 0;
      u.carryKind[k] = 0;
      u.haulT[k] = 0;
    }
  }
  w.buildings.kill(i);
  w.map.wallChanged();
  w.dirty.flow = true;
  w.dirty.jobs = true;
  w.dirty.districts = true;
}

export function buildingCentre(w: World, i: number): { x: number; y: number } {
  const def = buildingType(w.buildings.typeId[i]);
  return { x: w.buildings.tx[i] + def.w / 2, y: w.buildings.ty[i] + def.h / 2 };
}

/**
 * Does this tile refuse this unit's feet?
 *
 * The single statement of static solidity, shared by the movement pass
 * (`solid()` in `systems/movement.ts` adds only the bounds check) and by the
 * eviction below — one rule, two readers, so they cannot drift. Per-*unit*
 * because the exemptions are: a model may stand on the one wall tile it was
 * posted to, and a defender walks through his own gates.
 */
export function tileBlocks(w: World, tx: number, ty: number, i: number): boolean {
  const k = w.map.idx(tx, ty);
  /**
   * The battlefield scatter, first and cheapest.
   *
   * One null check in every settlement in the game and one array read on a
   * field. It is checked before the wall because a battlefield has almost no
   * wall on it and a great many trees, and this is the hottest predicate in the
   * movement pass.
   */
  const og = w.obstacleGrid;
  if (og !== null && (og[k] & OB_SOLID) !== 0) return true;
  if (w.map.resourceSolid?.[k]) return true;
  const wall = w.map.wall[k];
  if (wall !== WallKind.None) {
    if (alliedWith(w.units.team[i], w.wallTeam)) {
      const rawAt = w.units.wallAt[i];
      const at = rawAt < -1 ? -rawAt - 2 : rawAt;
      if (w.units.wallTile[i] === k || at === k) return false;
      if (at >= 0 && Math.abs(tx - at % MAP_W) + Math.abs(ty - Math.floor(at / MAP_W)) === 1) return false;
    }
    // The gate is the wall owner's door, not the player's personally: a
    // second banner fighting alongside the owner walks through it, and an
    // enemy of the town does not. `alliedWith` rather than `!areHostile`,
    // because a bystander has no more business inside the walls than a besieger.
    // `w.wallTeam` rather than `Team.Player`, because on an assault field the
    // town — and therefore the door — is the enemy's. See `World.wallTeam`.
    if (wall === WallKind.Gate && alliedWith(w.units.team[i], w.wallTeam)) return false;
    // The Gutter Congregation goes under. A `burrow` unit crosses curtain,
    // gate and tower tiles as if they were ground — under, not through, so
    // the wall takes no damage and the men *on* it stay unreachable ("your
    // walls are, from below, a suggestion"). Walls only, never buildings: a
    // keep has foundations. The lookup costs a typeId read, and only on the
    // rare branch where the proposed tile is wall at all.
    if (unitType(w.units.typeId[i]).ability === 'burrow') return false;
    return true;
  }
  return w.map.building[k] >= 0;
}

/**
 * Step everyone out of ground that has just been made solid.
 *
 * The collision pass can refuse a step into stone but it cannot *undo* one:
 * a unit whose tile turns solid under them — a foundation stamped where they
 * stand, a wall drawn along their street — fails every slide axis for ever
 * and freezes byte-identical for the rest of the run. The harness caught six
 * at once, two of them the very builders sent to the site that trapped them;
 * a wedged villager stays "nearest" to `staff()`'s gang search and holds the
 * builder budget for ever, which is the foundations stall recorded twice in
 * COORDINATION.md.
 *
 * So the stamp itself is the door: `placeBuilding` and the wall builders call
 * this over exactly the tiles they claimed. The nearest open tile is found by
 * ring search in a fixed scan order — pure state in, pure state out, so replays
 * hold. The garrison exemption rides in through `tileBlocks`: posting a man to
 * a tower does not throw him off it.
 *
 * This used to claim that solidity "cannot appear anywhere else, which is what
 * makes an eviction at the stamp complete rather than a per-tick sweep". That
 * was false and is now measured false — `src/tools/wedgecount.ts` still found
 * one to three wedged villagers per seed at the end of a balanced-bot run with
 * only this pass in place. `stepUnwedge` below is the net that catches the
 * rest. This stays anyway: being thrown clear the instant the wall goes up is
 * much better than being thrown clear within three seconds.
 */
export function evictFromRect(w: World, x0: number, y0: number, x1: number, y1: number): void {
  const u = w.units;
  for (let i = 0; i < u.slots.high; i++) {
    if (u.slots.alive[i] === 0) continue;
    if ((u.flags[i] & UF.Flying) !== 0) continue;
    const tx = Math.floor(u.x[i]);
    const ty = Math.floor(u.y[i]);
    if (tx < x0 || tx > x1 || ty < y0 || ty > y1) continue;
    if (!tileBlocks(w, tx, ty, i)) continue;
    const spot = nearestOpenTile(w, tx, ty, i, u.x[i], u.y[i]);
    if (!spot) continue;
    u.x[i] = spot.x + 0.5;
    u.y[i] = spot.y + 0.5;
    u.vx[i] = 0;
    u.vy[i] = 0;
  }
}

/**
 * How many slots the safety net looks at per tick.
 *
 * A full sweep of `MAX_UNITS` every 96 ticks — a little over three seconds of
 * simulated time, which is far quicker than a wedged unit is noticed and far
 * cheaper than testing everybody every tick.
 */
const UNWEDGE_PER_TICK = 64;

/**
 * The safety net under `evictFromRect`.
 *
 * The eviction at the stamp came with a claim: *"solidity cannot appear
 * anywhere else, which is what makes an eviction at the stamp complete rather
 * than a per-tick sweep."* That claim is false, and `src/tools/wedgecount.ts`
 * measures it false. Five seeds, a balanced bot, 9,000 ticks — with the stamp
 * eviction alone, three of the five ended with villagers standing inside
 * buildings (1, 1, 0, 3, 0) and every seed peaked above zero along the way.
 * With this net: zero at the end and zero at every sample, on all five. A
 * wedged villager stays "nearest" to `staff()`'s gang search and holds the
 * builder budget for ever, so each one of those is a settlement that quietly
 * stops finishing things.
 *
 * Rather than hunt the remaining doors one at a time — a settler ring that can
 * open on top of a tower, a shove from crowd separation, a rebuild-from-rubble
 * that stamps without evicting, and whatever the next one turns out to be —
 * this closes the whole class. It is deliberately a *net* and not a
 * replacement: `evictFromRect` still runs at every stamp, because being thrown
 * clear the instant the wall goes up is much better than being thrown clear
 * within three seconds.
 *
 * **Deterministic**, which it has to be, because it moves units and a position
 * is hashed state: the slice walked is a pure function of the tick, the scan
 * order inside `nearestOpenTile` is fixed, and nothing here reads a clock or an
 * `Rng`.
 */
export function stepUnwedge(w: World): void {
  const u = w.units;
  const high = u.slots.high;
  if (high === 0) return;
  const start = (w.tick * UNWEDGE_PER_TICK) % Math.max(1, high);
  for (let n = 0; n < UNWEDGE_PER_TICK; n++) {
    const i = (start + n) % high;
    if (u.slots.alive[i] === 0) continue;
    if ((u.flags[i] & UF.Flying) !== 0) continue;
    const tx = Math.floor(u.x[i]);
    const ty = Math.floor(u.y[i]);
    if (!tileBlocks(w, tx, ty, i)) continue;
    const spot = nearestOpenTile(w, tx, ty, i, u.x[i], u.y[i]);
    if (!spot) continue;
    u.x[i] = spot.x + 0.5;
    u.y[i] = spot.y + 0.5;
    u.vx[i] = 0;
    u.vy[i] = 0;
  }
}

/**
 * The closest tile this unit may stand on — closest to where the unit
 * actually *is*, not to the centre of the tile it is being pushed off.
 *
 * That distinction is the difference between an eviction and a teleport. A
 * grunt at the south edge of a wall tile stamped under him steps south, back
 * the way he came; measuring from the tile centre instead made the ring
 * search's scan order the tie-break, which sent everyone north — including
 * through the wall being built, an invader deposited *inside* the circuit by
 * the act of finishing it. A unit standing exactly on the centre still falls
 * to scan order, which is deterministic and vanishingly rare; a ring search
 * cannot promise which *side* of a line a unit lands on in every geometry
 * (that would take a flood fill), only that it lands on the nearest open
 * ground to its own feet.
 */
function nearestOpenTile(
  w: World,
  tx: number,
  ty: number,
  i: number,
  ux: number,
  uy: number,
): { x: number; y: number } | null {
  for (let r = 1; r <= 6; r++) {
    let bestX = -1;
    let bestY = -1;
    let bestD = Infinity;
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) continue;
        const x = tx + dx;
        const y = ty + dy;
        if (!inMap(x, y)) continue;
        if (tileBlocks(w, x, y, i)) continue;
        const d = (x + 0.5 - ux) ** 2 + (y + 0.5 - uy) ** 2;
        if (d < bestD) {
          bestD = d;
          bestX = x;
          bestY = y;
        }
      }
    }
    if (bestX >= 0) return { x: bestX, y: bestY };
  }
  return null;
}

export function setWall(w: World, tx: number, ty: number, kind: number, hp: number, tier = 1): void {
  if (!w.map.inBounds(tx, ty)) return;
  const k = w.map.idx(tx, ty);
  /**
   * `mods.wallHp` is applied *here*, and it was applied nowhere at all.
   *
   * Six tech nodes and three relics multiply it — Coursed Stone, Deep Footings,
   * Iron Gates, Breachwrights, The Great Wall — and every one of them was a
   * purchase that did nothing. Only the faction bonus was ever read, and it is
   * read further up, so nothing looked obviously missing.
   *
   * Applied at build time rather than at damage time on purpose: a wall that
   * gets tougher retroactively when you buy a tech would heal every breach in
   * the settlement at the moment of purchase. What you already built stands as
   * it was built.
   */
  const built = hp * w.mods.wallHp;
  // A curtain drawn through a wood takes the wood with it, the same way a
  // footprint does. Left standing, the tree would be a node inside stone: it
  // would still answer `nearestNodes`, and every worker sent to it would walk
  // into the wall until his patience ran out.
  const standing = w.nodes.at(k);
  if (standing >= 0) w.nodes.kill(standing);
  w.map.wall[k] = kind;
  w.map.wallMaxHp[k] = built;
  w.map.wallHp[k] = built;
  w.map.wallTier[k] = tier;
  // A wall drawn along somebody's street must not entomb them — the garrison
  // exemption (a model on its own posted tile) rides through `tileBlocks`.
  evictFromRect(w, tx, ty, tx, ty);
  w.dirty.flow = true;
  w.map.wallChanged();
}

export function clearWall(w: World, tx: number, ty: number): void {
  if (!w.map.inBounds(tx, ty)) return;
  const k = w.map.idx(tx, ty);
  w.map.wall[k] = WallKind.None;
  w.map.wallHp[k] = 0;
  w.map.wallMaxHp[k] = 0;
  w.map.wallTier[k] = 0;
  w.dirty.flow = true;
  w.map.wallChanged();
}

/**
 * How many heads this settlement has room for — and, for one people, what those
 * heads are grown in instead of beds.
 *
 * **The one door.** Every consumer of "how big may I be" reads `w.popCap` and
 * nothing else: `musterRoom` refuses against it, `stepPopulation` stops villagers
 * breeding at a share of it, the crowding term in `moraleTerms` measures against
 * it, the Splitmoot's press turns at it, and the HUD prints it. So the `groves`
 * binding is one fork here rather than a parallel capacity that four call sites
 * would have to learn about, and a race whose binding is not built falls through
 * to the sum it always was.
 *
 * The Choir's arithmetic, and each term is a sentence from its own passive:
 *
 *   *no houses* — a `civic` building with beds in it contributes nothing, and
 *   `raceBuildRefusal` refuses to let one be raised in the first place, so the
 *   player is told rather than left to work it out from a number that did not
 *   move. The Hearth is `core` and the barrack-rooms are `military`; both still
 *   count, because "you may not build a house" is not "you may not have a roof".
 *
 *   *groves compound* — "every completed grove raises the yield of all the
 *   others", which is a multiplier on the whole grove term rather than a bonus
 *   on the newest one, and is why the tenth ring is worth nearly three times the
 *   first.
 *
 *   *and die back through Hardfrost* — the grove term alone is seasonal, so a
 *   Choir's ceiling is a curve. Nothing else in the game has a population cap
 *   that falls, which is the point: winter is a bereavement here and a bad
 *   harvest everywhere else.
 */
export function recomputePopCap(w: World): void {
  const grown = race(w.raceId).binding === 'groves';
  let cap = 0;
  let groveTerm = 0;
  let groveCount = 0;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    if (b.team[i] !== Team.Player) continue;
    const def = buildingType(b.typeId[i]);
    if (def.popCap === 0) continue;
    const term = def.popCap * (1 + (b.tier[i] - 1) * 0.5);
    if (grown) {
      if (isHousing(def)) continue;
      if (isGrove(def)) {
        groveTerm += term;
        groveCount++;
        continue;
      }
    }
    cap += term;
  }
  if (groveCount > 0) {
    cap += groveTerm * (1 + GROVE_COMPOUND * (groveCount - 1)) * groveSeasonMul(w);
  }
  w.popCap = Math.floor(cap * (w.faction.bonuses.popCap ?? 1) * w.mods.popCap);
}

export function recomputeStorage(w: World): void {
  let cap = 300;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    if (b.team[i] !== Team.Player) continue;
    /**
     * On the same tier curve `recomputePopCap` uses, and it did not used to be.
     *
     * A tier of a building raised its beds, its Mana pool and its hitpoints and
     * left its shed exactly where it was — so upgrading a Vault, a Granary or
     * the Hearth bought no storage at all. That was invisible while the Hearth
     * upgrade was free and nothing else was ever upgraded; it stops being
     * invisible the moment the upgrade has a price, because the price is partly
     * *for* the shed. One term, shared with the beds, so the two cannot drift.
     */
    cap += buildingType(b.typeId[i]).storage * (1 + (b.tier[i] - 1) * 0.5);
  }
  /**
   * The Moonbound's No Hoard is applied here and nowhere else.
   *
   * To the *total* rather than to the per-building term, on purpose: a fifth of
   * everything — including the flat 300 the Hearth is worth and including
   * whatever tech and relics have added — is a people who genuinely cannot save.
   * A fifth of the building term alone would have left them a floor to bank
   * against, which is the exact thing the passive says they do not have.
   */
  w.resCap = (cap + w.mods.storage) * storageMul(w);
}

/** 1 for everybody but the Moonbound, who keep nothing. */
export function storageMul(w: World): number {
  return race(w.raceId).id === 'moonbound' ? 0.2 : 1;
}

/** Per level of the leader. The only thing tying the hero sheet to army size. */
export const MANA_PER_LEVEL = 18;
/**
 * What a Hearth alone is worth, so a Litany run can field its first company.
 *
 * **It stopped being worth that, and nothing said so.** At 60 the Litany could
 * raise *nothing at all* on tick zero — not one of the fifteen companies on its
 * roster, of any Age, at any strength. The cheapest banner in the game for them
 * is a single Pale Priest and it wants a pool of 68. Measured over twenty-four
 * Endless seeds with the balanced bot, a Litany run's median payroll was **0.0 a
 * minute**: it played every run, start to finish, with no soldiers, and was
 * overrun in 96% of them against the Ashen's 83%.
 *
 * Two changes walked past this constant without it. Establishments were tripled
 * — a Wretch's roll is eighty men, whose quarter-strength floor is twenty models
 * and therefore 160 of pool — and then the housing cut took the Hearth from
 * twenty-six beds to eighteen. `MANA_PER_THREAT`'s note still calibrates this
 * number against "the Hearth's twenty-six beds", which is the fingerprint of the
 * first miss and the reason the second went unnoticed.
 *
 * **Derived, not chosen.** A housed people opens able to raise one line company
 * at exactly the quarter floor — ten of a militia's forty. The same opening for
 * a pooled people is the cheapest line company at *its* floor: a Boneshield at
 * nine of thirty-six is 159, a Wretch at twenty of eighty is 160. So: 160.
 *
 * **Re-derive it whenever establishments or the Hearth's beds move.** It is a
 * parity figure between two bindings, not a free parameter, and the last two
 * times one side of that parity moved this number did not.
 */
export const MANA_BASE = 160;

/**
 * The pool, rebuilt from scratch — the discipline `recomputeStorage` and
 * `recomputePopCap` are under, for the same reason: anything accumulated in
 * place is erased the next time a building finishes.
 *
 * All four of the owner's Mana sources land here, and they are worth naming
 * against the sentence that asked for them:
 *
 *   *unique buildings*  `BuildingType.mana`, summed below and scaled by tier
 *                       exactly the way `popCap` is
 *   *research*          Academy nodes write `w.mods.manaMax`
 *   *character level*   `MANA_PER_LEVEL` per level of the leader — see the note
 *                       at `Mods.manaMax` for why this is the one capacity an
 *                       item may safely name as well
 *   *item passives*     items write `w.mods.manaMax` too
 *
 * Computed for every race and read by one, deliberately: see the note on
 * `World.mana`.
 */
export function recomputeManaCap(w: World): void {
  let cap = MANA_BASE;
  const b = w.buildings;
  for (let i = 0; i < b.slots.high; i++) {
    if (b.slots.alive[i] === 0) continue;
    if (b.build[i] < 1) continue;
    if (b.team[i] !== Team.Player) continue;
    const def = buildingType(b.typeId[i]);
    if (def.mana === 0) continue;
    cap += def.mana * (1 + (b.tier[i] - 1) * 0.5);
  }
  cap += w.mods.manaMax;
  cap += Math.max(0, w.hero.level - 1) * MANA_PER_LEVEL;
  w.manaMax = Math.floor(cap);
}

/**
 * The two layers, in order: the race's passives, then the house's.
 *
 * Order matters and is fixed rather than incidental. Both layers multiply into
 * the same bag, and floating-point multiplication is not associative — so a
 * world that folded the house first would differ from one that folded the race
 * first in the last bit of `unitDamage`, and a replay would diverge a hundred
 * ticks later. Race first, always, because the race is the thing the house is a
 * variation *on*.
 *
 * Called from `initRun` and from nowhere else, and deliberately *not* through
 * `foldPermanent`: at world creation the cart is empty, so `mods` and `modsBase`
 * are the same numbers and the same sequence of multiplications applied to each
 * produces the same bits. Writing both directly avoids a cycle — `foldPermanent`
 * lives in `systems/items.ts`, which imports this file — and there is nothing
 * for it to preserve yet. Identity is fixed for the life of a run; if that ever
 * stops being true, this needs the wrapper like everything else.
 */
export function foldIdentity(w: World): void {
  const layers: Array<Record<string, number>> = [];
  for (const rp of race(w.raceId).passives) if (rp.mods) layers.push(rp.mods);
  /**
   * The house layer arrives **only when a house was actually chosen**, and this
   * is a design decision rather than a null check.
   *
   * A race is what a settlement is whether or not anybody has said so — every
   * existing run, test and replay has always been *some* people, and
   * `raceOfFaction` merely names the one it already was. A leader is not like
   * that: a named character with two passives of his own is a thing the player
   * picked, and handing one to a run that never opened a selection screen would
   * silently change the balance of every session in the repository. It also
   * makes `w.leaderId === ''` mean exactly what it reads as, which is "nobody in
   * particular is in charge".
   *
   * `defaultLeaderFor` still exists and is still what `Cmd.PickKingdom` writes
   * when it is handed a bare race — the default is for *choosing*, not for
   * assuming.
   */
  const house = w.leaderId ? leader(w.leaderId) : undefined;
  for (const lp of house?.passives ?? []) if (lp.mods) layers.push(lp.mods);
  for (const bag of layers) {
    applyModBag(w.mods, bag);
    applyModBag(w.modsBase, bag);
  }
}

/**
 * The third identity layer: the family, on top of the people and the house.
 *
 * Same shape as `foldIdentity` and called immediately after it from `initRun`,
 * for the same reasons written there — at creation the cart is empty, so
 * writing `mods` and `modsBase` directly produces identical bits in each, and
 * going through `foldPermanent` would close a cycle through `items.ts`. The
 * bag order inside is canonical (catalogue order, see `resolveBloodline`), so
 * two runs configured with the same unlocks in a different id order fold to
 * the same bits.
 */
export function foldBloodline(w: World): void {
  for (const bag of bloodlineModBags(w.config.bloodline)) {
    applyModBag(w.mods, bag);
    applyModBag(w.modsBase, bag);
  }
}

/**
 * Fold a data-authored modifier bag into `w.mods`.
 *
 * `src/data` may not import `defaultMods()`, so a passive written there cannot
 * know whether the key it names multiplies or adds. The bag *does* know — the
 * value sitting in `defaultMods()` is 1 for a multiplier and 0 for an addend,
 * which is the same convention `items.ts` resolves against — so the decision is
 * made here, once, off the default rather than off a hand-kept list that would
 * rot the first time somebody added a modifier key.
 *
 * A key that is not in the bag at all is skipped rather than created. A passive
 * naming a modifier that does not exist is a typo, and a typo that silently
 * invents a field nothing reads is worse than one that does nothing at all.
 */
export function applyModBag(target: Mods, bag: Record<string, number>): void {
  const mods = target as unknown as Record<string, number>;
  const defaults = defaultMods() as unknown as Record<string, number>;
  for (const k of Object.keys(bag)) {
    if (!(k in defaults)) continue;
    if (defaults[k] === 1) mods[k] *= bag[k];
    else mods[k] += bag[k];
  }
}

/**
 * A company by id, in constant time.
 *
 * This was `w.companies.find(...)`, and it was called from inside the accept
 * predicate of the targeting sweep — so the cost was companies × candidates ×
 * soldiers × ticks. At a hundred companies it was invisible; at the several
 * thousand a city-sized army fields, it was most of the frame. The answer is
 * the same in every case; only the arithmetic to reach it changed.
 *
 * The index is a *derived cache* and is deliberately neither hashed nor saved:
 * it is rebuilt from `w.companies`, which is. Two guards keep it honest, and
 * they are belt and braces on purpose — a stale index here would be a
 * behaviour change, not merely a slow one:
 *
 *   Every site that adds to or replaces `w.companies` calls
 *   `invalidateCompanyIndex`, and
 *   every lookup validates the slot it lands on, rebuilding if the company
 *   sitting there is not the one asked for.
 */
/**
 * Is somebody actually giving this company orders?
 *
 * The player's own companies always; both sides on a shared field, because
 * there each is somebody's. Everything else steers itself. It lives here rather
 * than in `systems/companies.ts` because the *movement* pass needs the same
 * answer — a model that is being commanded follows its formation slot and a
 * model that is not navigates for itself — and two copies of that sentence in
 * two files is how the deployment truce came to need fixing three times.
 */
export function companyCommanded(w: World, c: Company): boolean {
  return c.team === Team.Player || w.battle?.pvp === true;
}

export function findCompany(w: World, id: number): Company | undefined {
  const idx = w.companyIdx.get(id);
  if (idx !== undefined) {
    const c = w.companies[idx];
    if (c !== undefined && c.id === id) return c;
  } else if (w.companyIdx.size === w.companies.length) {
    // The index is current and this id is genuinely not in it. A miss must be
    // as cheap as a hit, or a unit whose company has been retired reintroduces
    // the linear scan this exists to remove.
    return undefined;
  }
  const found = reindexCompanies(w).get(id);
  return found === undefined ? undefined : w.companies[found];
}

/** Mark the company index stale. Cheap; the rebuild is lazy. */
export function invalidateCompanyIndex(w: World): void {
  w.companyIdx.clear();
}

function reindexCompanies(w: World): Map<number, number> {
  const idx = w.companyIdx;
  idx.clear();
  for (let i = 0; i < w.companies.length; i++) idx.set(w.companies[i].id, i);
  return idx;
}

export function inMap(x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < MAP_W && y < MAP_H;
}
